// src/pages/Prontuarios.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../config/configApi";
import Swal from "sweetalert2";
import {
  BsPlus,
  BsTrash,
  BsArrowLeft,
  BsSave,
  BsFilePdf,
  BsList,
  BsSearch,
  BsEye,
  BsChevronDown,
  BsChevronUp,
  BsFileMedical,
} from "react-icons/bs";
import Cookies from "js-cookie";

const Prontuarios: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { nome, cpf } = location.state || {};

  // Estados do componente
  const [audioText, setAudioText] = useState("");
  const [error] = useState("");
  const [signature, setSignature] = useState("");
  const [signatureProfissional, setSignatureProfissional] = useState("");
  const [img, setUploadedImages] = useState<string[]>([]);
  const [imageIds, setImageIds] = useState<number[]>([]);
  const [fileInputText, setFileInputText] = useState<string>("");
  const [prontuariosComImagens, setProntuariosComImagens] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showList, setShowList] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProntuario, setSelectedProntuario] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    prontuariosImagens: false,
  });
  const [openProntuarios, setOpenProntuarios] = useState<{
    [key: string]: boolean;
  }>({});

  // Estados de paginação
  const [currentPages, setCurrentPages] = useState({
    prontuariosImagens: 1,
  });

  // Itens por página
  const itemsPerPageConfig = {
    prontuariosImagens: 6,
  };

  // MOVER OS HOOKS PARA CIMA DO RETORNO CONDICIONAL
  useEffect(() => {
    // Buscar o nome do usuário do cookie
    const username = Cookies.get("Username");

    if (username) {
      setSignatureProfissional(username);
    }
  }, []);

  const fetchProntuarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("Frontend");

      // Buscar todos os prontuários com imagens
      const prontuariosImagensRes = await api.get(
        "/api/prontuarios/com-imagens",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (prontuariosImagensRes.data.success) {
        // Filtrar apenas os prontuários do CPF específico
        const prontuariosFiltrados = prontuariosImagensRes.data.data.filter(
          (prontuario: any) => prontuario.nu_cpf === cpf,
        );

        setProntuariosComImagens(prontuariosFiltrados);
      }
    } catch (err) {
      console.error("Erro ao buscar prontuários:", err);
      Swal.fire({
        icon: "error",
        title: "Erro ao carregar prontuários",
        text: "Não foi possível carregar a lista de prontuários.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [cpf]); // Add cpf as dependency since it's used inside

  // Buscar prontuários quando o componente for montado ou quando o CPF mudar
  useEffect(() => {
    if (cpf) {
      fetchProntuarios();
    }
  }, [cpf, fetchProntuarios]); // Adicionar cpf como dependência
  const currentDate = new Date().toLocaleDateString("pt-BR");

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const newImages: string[] = [];

      if (img.length + filesArray.length <= 5) {
        filesArray.forEach((file) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            newImages.push(reader.result as string);
            if (newImages.length === filesArray.length) {
              setUploadedImages((prevImages) => [...prevImages, ...newImages]);
              // Adicionar null para novas imagens (sem ID)
              setImageIds((prevIds) => [
                ...prevIds,
                ...Array(filesArray.length).fill(null),
              ]);
            }
          };
          reader.readAsDataURL(file);
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Limite de fotos alcançado",
          text: "Você pode adicionar no máximo 5 fotos.",
          confirmButtonText: "Ok",
        });
      }
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    // Criar novos arrays sem o item removido
    const newImages = img.filter((_, index) => index !== indexToRemove);
    const newImageIds = imageIds.filter((_, index) => index !== indexToRemove);

    setUploadedImages(newImages);
    setImageIds(newImageIds);

    // Atualizar o texto do input de arquivo
    if (newImages.length === 0) {
      setFileInputText("");
    } else if (newImages.length === 1) {
      setFileInputText("1 arquivo selecionado");
    } else {
      setFileInputText(`${newImages.length} arquivos selecionados`);
    }
  };

  useEffect(() => {
    if (img.length === 0) {
      setFileInputText("");
    } else if (img.length === 1) {
      setFileInputText("1 arquivo selecionado");
    } else {
      setFileInputText(`${img.length} arquivos selecionados`);
    }
  }, [img]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const saveText = async () => {
    try {
      const token = localStorage.getItem("Frontend");

      // Converter a data do formato brasileiro para o formato MySQL (YYYY-MM-DD)
      const convertToMySQLDate = (dateString: string) => {
        const [day, month, year] = dateString.split("/");
        return `${year}-${month}-${day}`;
      };

      const mysqlDate = convertToMySQLDate(currentDate);

      // Preparar as imagens para envio - CORREÇÃO IMPORTANTE
      const imagensFormatadas = img.map((image, index) => {
        const imagemId = imageIds[index];

        // Verificar se é uma imagem existente que foi modificada
        const isExistingImage = imagemId !== null && imagemId !== undefined;
        const isModifiedImage =
          isExistingImage &&
          !image.startsWith("http://") &&
          !image.startsWith("https://");

        // Se é uma imagem existente E foi modificada, enviar para atualização
        if (isModifiedImage) {
          return {
            imagem_id: imagemId,
            img: image, // Enviar a nova versão da imagem
          };
        }
        // Se é uma imagem existente mas NÃO foi modificada, não enviar dados da imagem
        else if (isExistingImage) {
          return {
            imagem_id: imagemId,
            img: null, // Não enviar dados da imagem (já existe no servidor)
          };
        }
        // Se é uma nova imagem, enviar para inserção
        else {
          return {
            imagem_id: null,
            img: image, // Enviar a nova imagem
          };
        }
      });

      if (isEditing && selectedProntuario) {
        const payload = {
          id: selectedProntuario.id,
          nome: selectedProntuario.nome,
          nu_cpf: selectedProntuario.nu_cpf,
          cd_paciente: selectedProntuario.cd_paciente,
          text: audioText,
          signature: signature,
          date: mysqlDate,
          imagens: imagensFormatadas,
        };

        console.log("Enviando payload com imagens:", {
          ...payload,
          imagens: imagensFormatadas.map((img) => ({
            imagem_id: img.imagem_id,
            img: img.img ? "BASE64_IMAGE_DATA" : "NULL (não modificada)",
          })),
        });

        const response = await api.patch(
          `/api/prontuarios/com-imagens-atualizar/${selectedProntuario.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          await Swal.fire({
            icon: "success",
            title: "Prontuário atualizado com sucesso!",
            showConfirmButton: false,
            timer: 1500,
          });

          const generatePDFResponse = await Swal.fire({
            title: "Deseja gerar o PDF do prontuário?",
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
          });

          if (generatePDFResponse.isConfirmed) {
            generatePDF();
          }

          await Swal.fire({
            icon: "info",
            title: "Prontuário atualizado!",
            text: generatePDFResponse.isConfirmed
              ? "O PDF foi gerado com sucesso."
              : "O prontuário foi atualizado, mas o PDF não foi gerado.",
          });

          setAudioText("");
          setSignature("");
          setUploadedImages([]);
          setImageIds([]);
          setSelectedProntuario(null);
          setIsEditing(false);
          setShowList(true);

          // Atualizar a lista de prontuários
          fetchProntuarios();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Erro ao atualizar prontuário",
            text: "Houve um erro ao tentar atualizar o prontuário. Tente novamente.",
          });
        }
      }
    } catch (err) {
      console.error("Erro detalhado:", err);

      let errorMessage = `Ocorreu um erro ao tentar se conectar com o servidor: ${err}`;

      // Verificação segura para a propriedade response
      if (err && typeof err === "object" && "response" in err) {
        const errorWithResponse = err as { response?: { data?: unknown } };
        if (errorWithResponse.response?.data) {
          errorMessage = `Erro do servidor: ${JSON.stringify(
            errorWithResponse.response.data,
          )}`;
        }
      }

      await Swal.fire({
        icon: "error",
        title: "Erro ao conectar com o servidor",
        text: errorMessage,
      });
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    const logoUrl =
      "https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1";
    const logoWidth = 50;
    const logoHeight = 20;
    const logoX = (doc.internal.pageSize.width - logoWidth) / 2;
    const logoY = 10;

    doc.addImage(logoUrl, "PNG", logoX, logoY, logoWidth, logoHeight);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const title = "Prontuário de Atendimento";
    const titleWidth = doc.getTextWidth(title);
    const titleX = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(title, titleX, 43);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const pacienteNome =
      selectedProntuario?.nome || "Paciente não identificado";
    const pacienteText = `Paciente: ${pacienteNome}`;
    const pacienteWidth = doc.getTextWidth(pacienteText);
    const pacienteX = (doc.internal.pageSize.width - pacienteWidth) / 2;
    doc.text(pacienteText, pacienteX, 63);

    doc.setFontSize(10);
    doc.text("", 20, 75);

    const marginLeft = 20;
    const marginTop = 85;
    const pageWidth = doc.internal.pageSize.width - 40;
    const text = audioText;

    doc.setFontSize(10);
    doc.text(doc.splitTextToSize(text, pageWidth), marginLeft, marginTop);

    doc.setFontSize(12);
    doc.setFont("helvetica", "italic");

    const time = new Date().toLocaleTimeString("pt-BR");
    const signatureDateText = `Data: ${currentDate} Hora: ${time}`;

    const marginLeft2 = 20;
    const signatureDateY = doc.internal.pageSize.height - 40;
    const signatureTextY = signatureDateY + 6;
    const professionalSignatureY = signatureTextY + 6;

    doc.text(signatureDateText, marginLeft2, signatureDateY);
    doc.text(
      `Assinatura do responsável: ${signature}`,
      marginLeft,
      signatureTextY,
    );
    doc.text(
      `Assinatura do profissional: ${signatureProfissional}`,
      marginLeft,
      professionalSignatureY,
    );

    doc.save(`prontuario_${pacienteNome}.pdf`);
  };

  const toggleList = () => {
    // Se estiver editando, voltar para a lista
    if (isEditing) {
      setSelectedProntuario(null);
      setIsEditing(false);
      setAudioText("");
      setSignature("");
      setUploadedImages([]);
      setImageIds([]);
      setShowList(true);
    }
    // Não há mais alternância para "novo prontuário"
  };

  // Função para carregar um prontuário específico - CORRIGIDA
  const loadProntuario = async (prontuario: any) => {
    try {
      // Extrair URLs das imagens e seus IDs
      const imagensUrls: string[] = [];
      const ids: number[] = [];

      prontuario.imagens?.forEach((imgObj: any) => {
        // Verificar se é uma imagem base64 ou URL
        if (imgObj.imagem) {
          // Se já é base64, usar diretamente
          if (imgObj.imagem.startsWith("data:image/")) {
            imagensUrls.push(imgObj.imagem);
          } else {
            // Se é binário, converter para base64
            imagensUrls.push(`data:image/png;base64,${imgObj.imagem}`);
          }
          ids.push(imgObj.id);
        }
      });

      setSelectedProntuario(prontuario);
      setAudioText(prontuario.observacoes || prontuario.text || "");
      setSignature(
        prontuario.assinatura_responsavel || prontuario.signature || "",
      );
      setUploadedImages(imagensUrls);
      setImageIds(ids);
      setIsEditing(true);
      setShowList(false);
    } catch (error) {
      console.error("Erro ao carregar prontuário:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar o prontuário.",
      });
    }
  };

  // Função para alternar seção com comportamento de abrir/fechar
  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newState: { [key: string]: boolean } = {};

      // Fecha todas as outras seções ao abrir uma nova
      Object.keys(prev).forEach((key) => {
        newState[key] = key === section ? !prev[key] : false;
      });

      return newState;
    });

    // Resetar página ao abrir uma seção
    if (!openSections[section]) {
      setCurrentPages((prev) => ({
        ...prev,
        [section]: 1,
      }));
    }
  };

  // Função para alternar prontuário individual (collapse)
  const toggleProntuario = (id: string) => {
    setOpenProntuarios((prev) => {
      const newState = { ...prev };

      // Fecha todos os outros prontuários ao abrir um novo
      if (!prev[id]) {
        Object.keys(newState).forEach((key) => {
          newState[key] = false;
        });
      }

      newState[id] = !prev[id];
      return newState;
    });
  };

  // Filtrar prontuários baseado no termo de busca
  const filteredProntuariosComImagens = prontuariosComImagens.filter(
    (prontuario) =>
      prontuario.nome.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Funções de paginação para cada seção
  const handlePageChange = (section: string, page: number) => {
    setCurrentPages((prev) => ({
      ...prev,
      [section]: page,
    }));
  };

  // Componente de paginação reutilizável
  const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
  }: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-4">
        <div className="flex space-x-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Anterior
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 rounded-md text-sm ${
                currentPage === page
                  ? "bg-blue-700 text-white"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Próxima
          </button>
        </div>
      </div>
    );
  };

  // Função para voltar para /sad
  const handleBackToSad = () => {
    navigate("/Sad_Formulario");
  };

  // Se não houver dados do paciente, redireciona ou mostra mensagem
  // AGORA ESTE RETORNO CONDICIONAL VEM DEPOIS DE TODOS OS HOOKS
  if (!nome || !cpf) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center">
          <BsFileMedical className="text-6xl text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">
            Paciente não encontrado
          </h2>
          <p className="text-gray-500 mb-4">
            Volte à lista de pacientes e selecione um prontuário.
          </p>
          <button
            onClick={() => navigate("/sad")}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Voltar para SAD
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <motion.div
        className="flex justify-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
          alt="Logo Afrafepsaúde"
          className="w-60 h-auto"
        />
      </motion.div>

      <motion.div
        className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white flex justify-between items-center">
          {/* Lado esquerdo: Título e informações */}
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center">
                <BsFileMedical className="mr-3" />
                {isEditing ? "Editar Prontuário" : "Prontuário de Atendimento"}
              </h1>
              <p className="text-blue-100 mt-1">
                Gestão de informações do paciente
              </p>
            </div>
          </div>

          {/* Lado direito: Botões de ação */}
          <div className="flex gap-2 items-center">
            {isEditing && (
              <button
                onClick={toggleList}
                className="flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <BsList className="mr-2" />
                Ver Lista
              </button>
            )}

            {/* BOTÃO VOLTAR PARA SAD MOVIDO PARA A DIREITA */}
            <button
              onClick={handleBackToSad}
              className="flex items-center px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors border border-white/30"
            >
              <BsArrowLeft className="mr-2" />
              Voltar para SAD
            </button>
          </div>
        </div>

        {/* Informações do Paciente */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Dados do Paciente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  Nome Completo
                </label>
                <p className="text-lg font-semibold text-gray-800">{nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  CPF
                </label>
                <p className="text-lg font-semibold text-gray-800">{cpf}</p>
              </div>
            </div>
          </div>

          {showList ? (
            // Visualização de lista de prontuários
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-blue-800">
                  Lista de Prontuários
                </h2>
                <div className="relative w-64">
                  <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-900">
                    Carregando prontuários...
                  </p>
                </div>
              ) : (
                <>
                  {/* Prontuários com Imagens */}
                  <div className="mb-6">
                    <button
                      className="flex items-center justify-between w-full p-4 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                      onClick={() => toggleSection("prontuariosImagens")}
                    >
                      <h3 className="text-lg font-medium text-blue-700">
                        Prontuários
                        <span className="ml-2 text-sm bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                          {filteredProntuariosComImagens.length} itens
                        </span>
                      </h3>
                      {openSections.prontuariosImagens ? (
                        <BsChevronUp />
                      ) : (
                        <BsChevronDown />
                      )}
                    </button>

                    {openSections.prontuariosImagens && (
                      <div className="mt-3">
                        {filteredProntuariosComImagens.length > 0 ? (
                          <>
                            <div className="grid grid-cols-1 gap-3">
                              {filteredProntuariosComImagens
                                .slice(
                                  (currentPages.prontuariosImagens - 1) *
                                    itemsPerPageConfig.prontuariosImagens,
                                  currentPages.prontuariosImagens *
                                    itemsPerPageConfig.prontuariosImagens,
                                )
                                .map((prontuario) => (
                                  <div
                                    key={prontuario.id}
                                    className="p-4 border border-gray-200 rounded-lg"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-left">
                                        <p className="font-medium">
                                          {prontuario.nome}
                                        </p>
                                        <p className="text-sm text-gray-900">
                                          CPF: {prontuario.nu_cpf}
                                        </p>
                                        <p className="text-sm text-gray-900">
                                          DATA:{" "}
                                          {new Date(
                                            prontuario.data_registro,
                                          ).toLocaleDateString("pt-BR")}
                                        </p>

                                        <p className="text-sm text-gray-900">
                                          Imagens: {prontuario.imagens.length}
                                        </p>
                                      </div>
                                      <button
                                        onClick={() =>
                                          toggleProntuario(
                                            `imagem-${prontuario.id}`,
                                          )
                                        }
                                        className="text-blue-500 hover:text-blue-700"
                                      >
                                        {openProntuarios[
                                          `imagem-${prontuario.id}`
                                        ] ? (
                                          <BsChevronUp />
                                        ) : (
                                          <BsChevronDown />
                                        )}
                                      </button>
                                    </div>

                                    <div className="flex justify-end">
                                      <button
                                        onClick={() =>
                                          loadProntuario(prontuario)
                                        }
                                        className="flex items-center px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                                      >
                                        <BsEye className="mr-1" />
                                        Abrir
                                      </button>
                                    </div>

                                    {openProntuarios[
                                      `imagem-${prontuario.id}`
                                    ] && (
                                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="mb-3">
                                          <p className="text-sm text-gray-900">
                                            <strong>Observações:</strong>{" "}
                                            {prontuario.observacoes}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                          {prontuario.imagens.map(
                                            (imgObj: any, index: number) => (
                                              <img
                                                key={index}
                                                src={
                                                  imgObj.imagem
                                                    ? imgObj.imagem.startsWith(
                                                        "data:image/",
                                                      )
                                                      ? imgObj.imagem
                                                      : `data:image/png;base64,${imgObj.imagem}`
                                                    : "https://via.placeholder.com/80x80?text=Imagem"
                                                }
                                                alt={`Imagem ${index + 1}`}
                                                className="w-20 h-20 object-cover rounded-md border border-gray-300"
                                                onError={(e) => {
                                                  // Fallback para caso a imagem não carregue
                                                  e.currentTarget.src =
                                                    "https://via.placeholder.com/80x80?text=Imagem";
                                                }}
                                              />
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>

                            <Pagination
                              currentPage={currentPages.prontuariosImagens}
                              totalPages={Math.ceil(
                                filteredProntuariosComImagens.length /
                                  itemsPerPageConfig.prontuariosImagens,
                              )}
                              onPageChange={(page) =>
                                handlePageChange("prontuariosImagens", page)
                              }
                            />
                          </>
                        ) : (
                          <p className="text-gray-500 italic mt-3">
                            Nenhum prontuário encontrado.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            // Visualização do formulário de prontuário (edição ou novo)
            <>
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={() => {
                    setSelectedProntuario(null);
                    setIsEditing(false);
                    setAudioText("");
                    setSignature("");
                    setUploadedImages([]);
                    setImageIds([]);
                    setShowList(true);
                  }}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <BsArrowLeft className="mr-2" />
                  Voltar para Lista
                </button>

                {isEditing && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    Editando Prontuário
                  </span>
                )}
              </div>

              {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                  <p>{error}</p>
                </div>
              )}

              {/* REMOVIDO O BOTÃO DE ALTERNÂNCIA ENTRE MODO DIGITAÇÃO/GRAVAÇÃO */}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações do Atendimento
                </label>
                <textarea
                  value={audioText}
                  onChange={(e) => setAudioText(e.target.value)}
                  className="w-full h-48 p-4 text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite as observações do atendimento aqui..."
                />
              </div>

              {audioText.trim() && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assinatura do Responsável
                      </label>
                      <input
                        type="text"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Digite sua assinatura aqui..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assinatura do Profissional
                      </label>
                      <p className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
                        {signatureProfissional}
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Anexar Fotos (Máximo 5)
                    </label>
                    <div className="flex items-center gap-4">
                      {img.length < 5 && (
                        <label className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors">
                          <BsPlus className="mr-2" />
                          Escolher arquivos
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {fileInputText}
                    </p>

                    <div className="flex gap-4 flex-wrap mt-4">
                      {img.map((image, index) => (
                        <div
                          key={index}
                          className="relative w-24 h-24 overflow-hidden rounded-lg border border-gray-200 shadow-md"
                        >
                          <img
                            src={image}
                            alt={`Uploaded ${index}`}
                            className="object-cover w-full h-full"
                            onError={(e) => {
                              // Fallback para caso a imagem não carregue
                              e.currentTarget.src =
                                "https://via.placeholder.com/96x96?text=Imagem";
                            }}
                          />
                          <button
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition"
                          >
                            <BsTrash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* REMOVIDA A SEÇÃO DE CONTROLES DE GRAVAÇÃO */}

              {audioText.trim() && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={saveText}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <BsSave className="mr-2" />
                    {isEditing ? "Atualizar Prontuário" : "Salvar Prontuário"}
                  </button>
                  <button
                    onClick={generatePDF}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <BsFilePdf className="mr-2" />
                    Gerar PDF
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Prontuarios;
